import { FC } from 'react'

import { fileSize } from '@bettermode/common/filesize'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'

import { SupportedVideoFormats } from '../Composer/config/constants.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

import { FileUpload } from './FileUpload.js'
import { ImageLinkPicker } from './ImageLinkPicker.js'

export const VideoPickerModal: FC<{
  onEmbed: (url: string) => void
  onUpload: (file: Array<File>) => void
  open: boolean
  close: () => void
}> = ({ onEmbed, onUpload, open, close }) => {
  const { $t, formatList } = useI18n()
  const { data: network } = useNetwork()

  const helper = network?.settings?.fileSizeLimit && (
    <T
      id="FilePicker.HelperText"
      defaultMessage="The file size limit is {size_limit}. {br} Works with {extensions}"
      values={{
        size_limit: fileSize(network?.settings?.fileSizeLimit),
        extensions: formatList(['.MP4s']),
      }}
    />
  )

  return (
    <Modal open={open} onClose={close}>
      <Modal.Header
        title={$t({
          id: 'Picker.Video.Embed',
          defaultMessage: 'Embed video',
        })}
      />
      <Modal.Content>
        <div>
          <Tabs.Group>
            <Tabs.List variant="filled" fullWidth>
              <Tabs.Tab name="upload">
                <T id="Generics.Upload" defaultMessage="Upload" />
              </Tabs.Tab>
              <Tabs.Tab name="link">
                <T id="Generics.EmbedLink" defaultMessage="Embed link" />
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panels>
              <Tabs.Panel className="h-64">
                <FileUpload
                  onChange={onUpload}
                  accept={SupportedVideoFormats.join(',')}
                  label={$t({
                    id: 'Generics.ChooseAVideo',
                    defaultMessage: 'Choose a video',
                  })}
                  multiple
                  helper={helper}
                />
              </Tabs.Panel>
              <Tabs.Panel className="h-64">
                <ImageLinkPicker
                  onChange={onEmbed}
                  placeholder={$t({
                    id: 'Picker.Video.PasteLink.Placeholder',
                    defaultMessage: 'Paste a video link...',
                  })}
                  helper={$t({
                    id: 'Picker.Video.PasteLink.HelperText',
                    defaultMessage: 'Works with Youtube, Vimeo, and more',
                  })}
                />
              </Tabs.Panel>
            </Tabs.Panels>
          </Tabs.Group>
        </div>
      </Modal.Content>
    </Modal>
  )
}
